import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

interface LayoutChromeState {
  /** When true, the global mobile tab bar is hidden (e.g. for fullscreen chat). */
  mobileTabBarHidden: boolean;
  setMobileTabBarHidden: (hidden: boolean) => void;
  /**
   * When true, the main-content padding collapses to 0 on every layout
   * variant (desktop, web-mobile, native-mobile). Use it for screens that
   * need to claim the full content area — fullscreen chat, full-bleed
   * media, etc.
   */
  edgeToEdge: boolean;
  setEdgeToEdge: (edgeToEdge: boolean) => void;
  /** Open the mobile navigation drawer. No-op on desktop. */
  openMobileDrawer: () => void;
  /** Registered by the mobile layout variant on mount. */
  registerMobileDrawerOpener: (opener: (() => void) | null) => void;
  /**
   * Open the notification feed drawer (right side). Only wired up on native
   * mobile, where notifications render inside an in-layout `DrawerLayout`
   * to mirror the burger menu instead of a fullscreen Modal. On web /
   * desktop this is a no-op and consumers fall back to the Sheet Modal.
   */
  openNotificationFeed: () => void;
  /** True when the native notification drawer is registered by the layout. */
  notificationFeedAvailable: boolean;
  /** Registered by the native mobile layout variant on mount. */
  registerNotificationFeedOpener: (opener: (() => void) | null) => void;
}

const LayoutChromeContext = createContext<LayoutChromeState | null>(null);

export function LayoutChromeProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [mobileTabBarHidden, setMobileTabBarHidden] = useState(false);
  const [edgeToEdge, setEdgeToEdge] = useState(false);
  const [notificationFeedAvailable, setNotificationFeedAvailable] = useState(false);
  const drawerOpenerRef = useRef<(() => void) | null>(null);
  const notificationOpenerRef = useRef<(() => void) | null>(null);

  const registerMobileDrawerOpener = useCallback((opener: (() => void) | null) => {
    drawerOpenerRef.current = opener;
  }, []);

  const openMobileDrawer = useCallback(() => {
    drawerOpenerRef.current?.();
  }, []);

  const registerNotificationFeedOpener = useCallback((opener: (() => void) | null) => {
    notificationOpenerRef.current = opener;
    setNotificationFeedAvailable(opener !== null);
  }, []);

  const openNotificationFeed = useCallback(() => {
    notificationOpenerRef.current?.();
  }, []);

  return (
    <LayoutChromeContext.Provider
      value={{
        mobileTabBarHidden,
        setMobileTabBarHidden,
        edgeToEdge,
        setEdgeToEdge,
        openMobileDrawer,
        registerMobileDrawerOpener,
        openNotificationFeed,
        notificationFeedAvailable,
        registerNotificationFeedOpener,
      }}
    >
      {children}
    </LayoutChromeContext.Provider>
  );
}

export function useLayoutChrome(): LayoutChromeState {
  const ctx = useContext(LayoutChromeContext);
  // Tolerate missing provider so tests/storybook don't have to set it up.
  // Falls back to a no-op so consumers never crash, just don't get to
  // toggle global chrome.
  if (!ctx) {
    return {
      mobileTabBarHidden: false,
      setMobileTabBarHidden: () => {},
      edgeToEdge: false,
      setEdgeToEdge: () => {},
      openMobileDrawer: () => {},
      registerMobileDrawerOpener: () => {},
      openNotificationFeed: () => {},
      notificationFeedAvailable: false,
      registerNotificationFeedOpener: () => {},
    };
  }
  return ctx;
}

/**
 * Hide the mobile tab bar for the current screen. Restores it on unmount.
 * Use inside a screen component to opt that screen into fullscreen mode.
 */
export function useHideMobileTabBar(enabled = true) {
  const { setMobileTabBarHidden } = useLayoutChrome();
  const set = useCallback(
    (value: boolean) => setMobileTabBarHidden(value),
    [setMobileTabBarHidden],
  );

  useEffect(() => {
    if (!enabled) return;
    set(true);
    return () => set(false);
  }, [enabled, set]);
}

/**
 * Collapse the main-area content padding to 0 across all layout variants
 * (desktop, web-mobile, native-mobile) so the screen renders edge-to-edge.
 * Restores on unmount. Use for fullscreen surfaces like chat.
 */
export function useEdgeToEdge(enabled = true) {
  const { setEdgeToEdge } = useLayoutChrome();
  const set = useCallback((value: boolean) => setEdgeToEdge(value), [setEdgeToEdge]);

  useEffect(() => {
    if (!enabled) return;
    set(true);
    return () => set(false);
  }, [enabled, set]);
}

/**
 * @deprecated Use `useEdgeToEdge` instead. The flag now applies to every
 * layout variant, not just mobile.
 */
export const useMobileEdgeToEdge = useEdgeToEdge;
